import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Bank } from 'src/app/model/bank';
import { CommonService } from 'src/app/services/common.service';
import { StorageService } from 'src/app/services/storage.service';

@Component({
    selector: 'app-user-bank-data',
    templateUrl: './user-bank-data.component.html',
    styleUrls: ['./user-bank-data.component.css'],
})
export class UserBankDataComponent implements OnInit, OnChanges, OnDestroy {
    private readonly destroy$ = new Subject<void>();

    edit = false;
    invalidForm = false;

    form: FormGroup;

    accountTypeDropdown: string[] = [];
    bankList: Bank[] = [];
    bankNameList: string[] = [];

    @Input() bank: Bank = new Bank();
    @Input() hideHeader = false;
    @Input() hideEdit = false;

    @Output() update = new EventEmitter<FormGroup>();

    constructor(
        private readonly commonService: CommonService,
        private readonly formBuilder: FormBuilder,
        private readonly storageService: StorageService,
    ) {
        this.form = this.formBuilder.group({
            userRoleId: [null],
            bankId: [null],
            bankName: ['', Validators.required],
            bankCode: ['', [Validators.required, Validators.maxLength(10)]],
            branchCode: ['', Validators.required],
            bankLocation: ['', [Validators.required, Validators.maxLength(35)]],
            accountNumber: ['', [Validators.required, Validators.maxLength(20)]],
            accountHolderName: ['', [Validators.required, Validators.maxLength(70)]],
            accountType: ['', Validators.required],
            bankDetailId: [null],
        });

        this.loadAccountTypes();
        this.loadBanks();
    }

    ngOnInit(): void {
        this.form
            .get('bankName')
            ?.valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe((bankName: string) => {
                this.setBranchCode(bankName);
                this.setBankCode(bankName);
            });

        if (this.hideHeader) {
            const element = document.getElementsByName('bank-model');
            element.forEach((p) => p.classList.add('modify-padding'));
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.bank?.currentValue !== changes.bank?.previousValue) {
            this.setForm(changes.bank.currentValue);
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ================================
    // 🔹 Load dropdown data (cached)
    // ================================
    private loadAccountTypes(): void {
        if (this.storageService.accountTypeDropdown.value) {
            this.accountTypeDropdown = this.storageService.accountTypeDropdown.value;
            return;
        }

        this.commonService.getBankAccountType().subscribe((res) => {
            this.accountTypeDropdown = res.data || [];
            this.storageService.accountTypeDropdown.next(this.accountTypeDropdown);
        });
    }

    private loadBanks(): void {
        if (this.storageService.bankList.value) {
            this.bankList = this.storageService.bankList.value;
            this.bankNameList = this.storageService.bankNameList.value;
            return;
        }

        this.commonService.getBanks().subscribe((res) => {
            this.bankList = res || [];
            this.bankNameList = this.bankList.map((b) => b.bankName);

            this.storageService.bankList.next(this.bankList);
            this.storageService.bankNameList.next(this.bankNameList);
        });
    }

    // ================================
    // 🔹 Form handling
    // ================================
    private setForm(bank: Bank): void {
        if (!bank) return;

        this.form.patchValue({
            bankId: bank.bankId ?? null,
            bankName: bank.bankName ?? '',
            bankCode: bank.bankCode ?? '',
            branchCode: bank.branchCode ?? '',
            bankLocation: bank.bankLocation ?? '',
            accountNumber: bank.accountNumber ?? '',
            accountHolderName: bank.accountHolderName ?? '',
            accountType: bank.accountType ?? '',
            bankDetailId: bank.bankDetailId ?? null,
        });
    }

    changeView(): void {
        this.edit = !this.edit;
        this.setForm(this.bank);
    }

    // ================================
    // 🔹 Helpers
    // ================================
    private setBranchCode(bankName: string): void {
        const bank = this.bankList.find((b) => b.bankName === bankName);
        if (bank) {
            this.form.get('branchCode')?.setValue(bank.branchCode);
        }
    }

    private setBankCode(bankName: string): void {
        const bank = this.bankList.find((b) => b.bankName === bankName);
        if (bank) {
            this.form.get('bankCode')?.setValue(bank.bankCode);
        }
    }

    // ================================
    // 🔹 Submit
    // ================================
    submit(): void {
        if (this.form.valid) {
            this.invalidForm = false;
            this.edit = false;
            this.update.emit(this.form);
            return;
        }

        this.invalidForm = true;
        this.form.markAllAsTouched();
    }
}
